/**
 * `lib/text.ts` — how wide a string is drawn, and where to break it (launch round,
 * 7 Oct 2026: "some of the labels are overflowing on bars and not visually pleasing").
 *
 * SVG has no `text-overflow` and no wrapping, so a chart that wants its row names whole has
 * to know their width before it lays out. The browser answers through one off-screen
 * canvas (`CanvasRenderingContext2D.measureText`), cached per font and string because a
 * chart re-renders on every hover. jsdom has no canvas, so tests and any environment
 * without one get a deterministic estimate instead: `0.6 × font size × characters`, which
 * is a little wider than the real glyphs and so errs on the side of room.
 */

/** Width of one character as a share of the font size, where nothing can measure. */
export const TEXT_FALLBACK_RATIO = 0.6;

/** The portal's sans and mono stacks, as `tokens.css` spells them. */
export const FONT_SANS = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Inter, Arial, sans-serif';
export const FONT_MONO = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';

let ctx: CanvasRenderingContext2D | null | undefined;
const cache = new Map<string, number>();

function context(): CanvasRenderingContext2D | null {
  if (ctx !== undefined) return ctx;
  ctx = null;
  try {
    // jsdom answers `getContext` with a "not implemented" console error; skip it outright.
    if (typeof document === 'undefined' || /jsdom/i.test(navigator.userAgent)) return ctx;
    ctx = document.createElement('canvas').getContext('2d');
  } catch {
    ctx = null;
  }
  return ctx;
}

/** The font size a CSS font shorthand names (`700 11px …` → 11), or 12 when it names none. */
function fontSize(font: string): number {
  const m = /(\d+(?:\.\d+)?)px/.exec(font);
  return m ? Number(m[1]) : 12;
}

/** Width of `text` drawn in `font` (a CSS font shorthand, e.g. `700 11px ui-monospace`). */
export function measureText(text: string, font: string): number {
  if (!text) return 0;
  const key = `${font}\u0000${text}`;
  const hit = cache.get(key);
  if (hit !== undefined) return hit;
  const c = context();
  let width: number;
  if (c) {
    c.font = font;
    width = c.measureText(text).width;
  } else {
    width = TEXT_FALLBACK_RATIO * fontSize(font) * text.length;
  }
  if (cache.size > 4000) cache.clear();
  cache.set(key, width);
  return width;
}

/** The font shorthand for a chart's text: size in px, weight, and sans or mono. */
export function chartFont(size: number, weight: number | string = 400, mono = false): string {
  return `${weight} ${size}px ${mono ? FONT_MONO : FONT_SANS}`;
}

/** Width of `text` at `size` px in the chart's sans (or mono) font. */
export function textWidth(text: string, size: number, weight: number | string = 400, mono = false): number {
  return measureText(text, chartFont(size, weight, mono));
}

/**
 * `text` broken into lines no wider than `width`, at spaces and after hyphens or slashes.
 *
 * Aims for `maxLines` (two, for a row label). A name that still does not fit in that many
 * lines keeps every word and takes another line rather than losing its end: the client's
 * rule is "never truncated", and a third line in a rare row is cheaper than a name a reader
 * cannot finish. A single word wider than `width` sits on its own line whole.
 */
export function wrapText(text: string, width: number, font: string, maxLines = 2): string[] {
  const clean = text.replace(/\s+/g, ' ').trim();
  if (!clean) return [''];
  if (measureText(clean, font) <= width) return [clean];
  // Break points: after a space (dropped) or after a hyphen / slash (kept).
  const words = clean.match(/[^\s/-]+[/-]?|[/-]/g) ?? [clean];
  const joins = (prev: string) => (/[/-]$/.test(prev) ? '' : ' ');
  const lines: string[] = [];
  let line = '';
  for (const word of words) {
    const next = line ? `${line}${joins(line)}${word}` : word;
    if (!line || measureText(next, font) <= width) {
      line = next;
    } else {
      lines.push(line);
      line = word;
    }
  }
  if (line) lines.push(line);
  if (lines.length <= maxLines) return lines;
  /*
    Too many lines at `width`: balance the words over `maxLines` lines instead (each a
    little wider than asked), as long as that stays within 1.25× the width. Past that the
    greedy lines stand, one more of them.
  */
  const balanced = balance(words, maxLines, font, joins);
  const widest = Math.max(...balanced.map((l) => measureText(l, font)));
  return widest <= width * 1.25 ? balanced : lines;
}

/** `words` over `n` lines with the widest line as narrow as a greedy split can make it. */
function balance(words: string[], n: number, font: string, joins: (prev: string) => string): string[] {
  const full = words.reduce((acc, w) => (acc ? `${acc}${joins(acc)}${w}` : w), '');
  const target = measureText(full, font) / n;
  const lines: string[] = [];
  let line = '';
  for (const word of words) {
    const next = line ? `${line}${joins(line)}${word}` : word;
    if (line && measureText(line, font) >= target && lines.length < n - 1) {
      lines.push(line);
      line = word;
    } else {
      line = next;
    }
  }
  if (line) lines.push(line);
  return lines;
}
